import _ from "lodash";
import {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { connect } from "react-redux";

import actions from "@/actions";
import { Select } from "@/components";
import type { Light } from "@/models/index";
import type { MarkerPosition } from "@/models/marker-position";

import Panel from "../base/Panel";

interface SearchPanelProps {
    setSearchedLights: (lights: Light[]) => void;
    opened?: boolean;
    searchedLights: Light[];
    lights: Light[];
    markers: Record<string, MarkerPosition>;
    selectMatches: (
        filteredItems: Light[],
        filterType: string,
        filteredMarkers?: MarkerPosition[],
    ) => void;
}

const SearchPanel = ({
    setSearchedLights,
    opened,
    searchedLights,
    lights,
    markers,
    selectMatches,
}: SearchPanelProps): ReactNode => {
    const inputSearchBleVLCRef = useRef(null);
    const [selectedOption, setSelectedOption] = useState("contains");
    const [prevOpened, setPrevOpened] = useState(opened);
    const [searchedMarkers, setSearchedMarkers] = useState<MarkerPosition[]>(
        [],
    );

    const runSearch = useMemo(
        () =>
            _.debounce(setSearchedLights, 500, {
                trailing: true,
                leading: false,
            }),
        [setSearchedLights],
    );

    const searchByOptions = useCallback(
        (input, option) => {
            const search = [];
            switch (option) {
                case "equals":
                    _.filter(lights, (l) => {
                        const value = _.toNumber(input);
                        const vlcId = _.toNumber(l.id);
                        const bleId = _.toNumber(l.bleId);
                        if (
                            _.isEqual(vlcId, value) ||
                            _.isEqual(bleId, value)
                        ) {
                            search.push(l);
                        }
                    });
                    break;
                case "end":
                    if (input !== "") {
                        _.filter(lights, (l) => {
                            const vlcId = _.toString(l.id);
                            const bleId = _.toString(l.bleId);
                            if (
                                _.endsWith(vlcId, input) ||
                                _.endsWith(bleId, input)
                            ) {
                                search.push(l);
                            }
                        });
                    }
                    break;
                case "start":
                    if (input !== "") {
                        _.filter(lights, (l) => {
                            const vlcId = _.toString(l.id);
                            const bleId = _.toString(l.bleId);
                            if (
                                _.startsWith(vlcId, input) ||
                                _.startsWith(bleId, input)
                            ) {
                                search.push(l);
                            }
                        });
                    }
                    break;
                default:
                    if (input !== "") {
                        _.filter(lights, (l) => {
                            const vlcId = _.toString(l.id);
                            const bleId = _.toString(l.bleId);
                            if (
                                _.includes(vlcId, input) ||
                                _.includes(bleId, input)
                            ) {
                                search.push(l);
                            }
                        });
                    }
                    break;
            }

            const markerMatches: MarkerPosition[] = [];

            if (input !== "") {
                for (const marker of Object.values(markers)) {
                    const identifier = marker.identifier ?? "";

                    switch (option) {
                        case "equals": {
                            if (identifier === input) {
                                markerMatches.push(marker);
                            }
                            break;
                        }
                        case "end": {
                            if (_.endsWith(identifier, input)) {
                                markerMatches.push(marker);
                            }
                            break;
                        }
                        case "start": {
                            if (_.startsWith(identifier, input)) {
                                markerMatches.push(marker);
                            }
                            break;
                        }
                        default: {
                            if (_.includes(identifier, input)) {
                                markerMatches.push(marker);
                            }
                            break;
                        }
                    }
                }
            }

            runSearch(search);
            setSearchedMarkers(markerMatches);
        },
        [lights, markers, runSearch],
    );

    const handleChange = useCallback(() => {
        searchByOptions(inputSearchBleVLCRef.current.value, selectedOption);
    }, [searchByOptions, selectedOption]);

    useEffect(() => {
        if (opened && !prevOpened) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSelectedOption("contains");
        } else if (!opened && prevOpened) {
            setSearchedLights([]);
            setSearchedMarkers([]);
            if (inputSearchBleVLCRef.current) {
                inputSearchBleVLCRef.current.value = "";
            }
            setSelectedOption(null);
        }
        setPrevOpened(opened);
    }, [opened, prevOpened, setSearchedLights]);

    const options = [
        { value: "contains", label: "Contains" },
        { value: "equals", label: "Equals" },
        { value: "end", label: "Ends With" },
        { value: "start", label: "Starts With" },
    ];

    const matchCount = searchedLights.length + searchedMarkers.length;

    return (
        <Panel title="Search">
            <div className="search-panel">
                <div className="input-wrapper">
                    <i className="far fa-search fa-2x input-search-icon" />
                    <input
                        className="input-search-ble-vlc"
                        type="text"
                        placeholder="Search by ID or identifier"
                        onChange={handleChange}
                        ref={inputSearchBleVLCRef}
                    />
                    <Select
                        searchable
                        options={options}
                        className="dropdown-search-panel"
                        classNamePrefix="dropdown-select"
                        value={selectedOption}
                        onChange={(option) => {
                            if (
                                option &&
                                "value" in option &&
                                typeof option.value === "string"
                            ) {
                                setSelectedOption(option.value);
                            }

                            handleChange();
                        }}
                    />
                </div>
                <button
                    type="button"
                    disabled={matchCount === 0}
                    className="btn btn-primary select-matches"
                    onClick={() =>
                        selectMatches(searchedLights, "search", searchedMarkers)
                    }
                >
                    Select Matches
                </button>
                <div className="counterWrapper">
                    <div className="countMatches">
                        {`${matchCount} matches`}
                    </div>
                </div>
            </div>
        </Panel>
    );
};

const mapStateToProps = ({ map }) => ({
    searchedLights: map.general.searchedLights,
    lights: _.values(map.values.global.present.lights),
    markers: map.values.global.present.markers,
});

const mapDispatchToProps = {
    setSearchedLights: actions.map.general.setSearchedLights,
    selectMatches: actions.map.general.selectMatches,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchPanel);
